import { z } from "zod";
import { currency, email, phone, slug, timezone, trimmed } from "./common";

export const BRANCH_FORMATS = ["dine_in", "takeaway", "delivery", "self_service"] as const;

const dayHours = z
  .object({ open: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), close: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/) })
  .nullable();
export const workingHours = z.object({
  mon: dayHours, tue: dayHours, wed: dayHours, thu: dayHours, fri: dayHours, sat: dayHours, sun: dayHours,
});

/** Точка филиала на карте (WGS-84): оба поля или null — «не указано». */
export const geoPoint = z.strictObject({
  lat: z.number({ error: "Укажите широту" }).min(-90, { error: "Широта — от −90 до 90" }).max(90, { error: "Широта — от −90 до 90" }),
  lng: z.number({ error: "Укажите долготу" }).min(-180, { error: "Долгота — от −180 до 180" }).max(180, { error: "Долгота — от −180 до 180" }),
});
export type GeoPoint = z.infer<typeof geoPoint>;

export const branchCreateSchema = z.strictObject({
  name: trimmed(2, 120),
  slug,
  address: trimmed(0, 300).optional(),
  city: trimmed(0, 120).optional(),
  phone: phone.optional(),
  email: email.optional(),
  timezone,
  currency,
  dayCutoff: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: "Формат ЧЧ:ММ" }).default("05:00"),
  formats: z.array(z.enum(BRANCH_FORMATS)).min(1, { error: "Выберите хотя бы один формат" }).default(["dine_in"]),
  workingHours: workingHours.optional(),
  /** Где филиал на карте: страница «Рядом» на сайте Coca и маршрут на сайте заведения */
  location: geoPoint.nullable().optional(),
  isActive: z.boolean().default(true),
});
export const branchUpdateSchema = branchCreateSchema.partial().extend({ version: z.number().int().min(1) });
export type BranchCreateInput = z.input<typeof branchCreateSchema>;
export type BranchUpdateInput = z.input<typeof branchUpdateSchema>;

/** Ссылки «Построить маршрут» по точке: 2ГИС, Google Maps, Яндекс Карты. */
export function routeLinks(p: GeoPoint) {
  return {
    twogis: `https://2gis.kg/geo/${p.lng},${p.lat}`,
    google: `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`,
    yandex: `https://yandex.ru/maps/?rtext=~${p.lat},${p.lng}`,
  };
}

/** Расстояние по прямой, км (гаверсинус). */
export function distanceKm(a: GeoPoint, b: GeoPoint): number {
  const r = (d: number) => (d * Math.PI) / 180;
  const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

export const companyUpdateSchema = z.strictObject({
  name: trimmed(2, 120).optional(),
  legalName: trimmed(0, 200).nullable().optional(),
  taxId: z.string().trim().regex(/^\d{10,14}$/, { error: "ИНН — 10–14 цифр" }).nullable().optional(),
  timezone: timezone.optional(),
  defaultLocale: z.enum(["ru", "ky", "en", "uz"]).optional(),
});

export type Branch = {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  timezone: string;
  currency: string;
  dayCutoff: string;
  formats: string[];
  location: GeoPoint | null;
  isActive: boolean;
  version: number;
  createdAt: string;
};
